import i18next from 'i18next';
import { showSelectOptionModal } from '../../common/Dialog/Dialog';
import { useModal } from '../../common/ui-kit/modal/useModal';
import { ButtonColor } from '../../common/ui-kit/buttons/Button';

function t(value: string, options?: any): string {
  return i18next.t(
    `translation:subtenants.list.${value}` as any,
    options,
  ) as any as string;
}

type RemoveConfirmResult = {
  removeSubtenant: boolean;
  movePatients: boolean;
  selectedSubtenant?: string;
};

const cancelRemoveResult = {
  removeSubtenant: false,
  movePatients: false,
};

const removeSubtenantWithPatientsResult = {
  removeSubtenant: true,
  movePatients: false,
};

async function confirmMovePatients(
  modals: ReturnType<typeof useModal>,
  subtenants: string[],
): Promise<string | null> {
  const subtenant = await showSelectOptionModal(modals, {
    title: t('confirmations.move_patients.title'),
    text: t('confirmations.move_patients.text'),
    okButtonText: t('confirmations.move_patients.ok_button'),
    options: subtenants,
  });
  if (!subtenant) return null;
  return subtenant;
}

async function confirmRemoveSubtenantWithoutPatients(
  modals: ReturnType<typeof useModal>,
  subtenantName: string,
): Promise<boolean> {
  return await modals.showDeletionConfirmation({
    title: t('confirmations.remove.title'),
    text: t('confirmations.remove.text_no_patients', {
      subtenant: subtenantName,
    }),
    okButtonText: t('confirmations.remove.ok_button'),
  });
}

async function confirmRemoveSubtenantWithPatients(
  modals: ReturnType<typeof useModal>,
  subtenantName: string,
): Promise<boolean> {
  return await modals.showDeletionConfirmation({
    title: t('confirmations.remove.title'),
    text: t('confirmations.remove.text_delete_patients', {
      subtenant: subtenantName,
    }),
    okButtonText: t('confirmations.remove.ok_button'),
  });
}

async function confirmRemoveSubtenantWithOptions(
  modals: ReturnType<typeof useModal>,
  subtenantName: string,
  subtenants: string[],
): Promise<RemoveConfirmResult> {
  const isOneSubtenantOption = subtenants.length === 1;
  const remove_subtenant_options: string[] = [
    'delete_patients',
    isOneSubtenantOption ? 'move_patients_one_subtenant' : 'move_patients',
  ];

  const option = await showSelectOptionModal(modals, {
    title: t('confirmations.remove.title'),
    text: t('confirmations.remove.text_select_option', {
      subtenant: subtenantName,
    }),
    okButtonText: t('confirmations.remove.ok_button'),
    okButtonColor: ButtonColor.SuperDanger,
    options: remove_subtenant_options.map((option) =>
      t(`options.${option}` as any, { subtenant: subtenants[0] }),
    ),
  });
  if (
    option ===
    t(`options.move_patients_one_subtenant` as any, {
      subtenant: subtenants[0],
    })
  ) {
    return {
      removeSubtenant: true,
      movePatients: true,
      selectedSubtenant: subtenants[0],
    };
  }
  if (option === t(`options.delete_patients` as any)) {
    return removeSubtenantWithPatientsResult;
  }
  if (option === t(`options.move_patients` as any)) {
    const subtenant = await confirmMovePatients(modals, subtenants);
    if (subtenant) {
      return {
        removeSubtenant: true,
        movePatients: true,
        selectedSubtenant: subtenant,
      };
    }
  }
  return cancelRemoveResult;
}

export async function confirmRemoveSubtenant(
  modals: ReturnType<typeof useModal>,
  subtenantName: string,
  subtenants: string[],
  isPatientsExist: boolean,
): Promise<RemoveConfirmResult> {
  if (subtenants.length === 0) {
    await modals.showAlert({
      text: t('confirmations.cant_remove_last_subtenant'),
    });
    return cancelRemoveResult;
  }
  if (!isPatientsExist) {
    const isConfirmed = await confirmRemoveSubtenantWithoutPatients(
      modals,
      subtenantName,
    );
    return isConfirmed ? removeSubtenantWithPatientsResult : cancelRemoveResult;
  }

  return await confirmRemoveSubtenantWithOptions(
    modals,
    subtenantName,
    subtenants,
  );
}
